// ─── Client-side calendar assembly ─────────────────────────────────────────────
// Builds CalendarProps for the operator's default "today" view WITHOUT going
// through the AI. Mirrors the booking→event mapping the AI performs server-side
// (compactBookingForLlm in v1-chat): resource_name joins machine names, and
// resource_order is the min display_order so day-view columns order correctly.
//
// TIME MODEL: the day view treats event.start as a *naive local wall-clock*
// string — getDatePart slices "YYYY-MM-DD" off the front and minutesFromMidnight
// reads the hour via new Date(iso).getHours(). Bookings are stored in UTC, so we
// convert every timestamp into the tenant-local wall clock (no Z) before handing
// it to the renderer.

import type {
  BookingStatus,
  CalendarEvent,
  CalendarProps,
  CalendarResource,
  PaymentStatus,
} from './render'
import type { BookingRecord, ResourceRecord } from './api'

// Convert a UTC instant (ISO string with Z/offset) into a naive wall-clock
// string in the given IANA timezone: "YYYY-MM-DDTHH:mm:ss" (no Z).
// The API now returns tenant-local naive datetimes — those pass through
// unchanged (re-converting a wall clock would shift it by the tz offset).
export function zonedWallClock(utcIso: string, timezone: string): string {
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(utcIso)) {
    return utcIso.length === 16 ? `${utcIso}:00` : utcIso
  }
  const d = new Date(utcIso)
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d)
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '00'
  let hour = get('hour')
  if (hour === '24') hour = '00' // some engines emit 24 for midnight
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}:${get('second')}`
}

// Today's date (YYYY-MM-DD) in the tenant timezone.
export function todayLocalDate(timezone: string): string {
  return zonedWallClock(new Date().toISOString(), timezone).slice(0, 10)
}

// Same, falling back to the browser's own zone while the tenant's is not yet known.
export function todayInZone(timezone: string | null | undefined): string {
  return todayLocalDate(timezone || Intl.DateTimeFormat().resolvedOptions().timeZone)
}

// Shift a YYYY-MM-DD string by whole days without touching the browser timezone.
export function shiftLocalDate(localDate: string, days: number): string {
  const [y, m, d] = localDate.split('-').map(Number)
  const shifted = new Date(Date.UTC(y, m - 1, d + days))
  return shifted.toISOString().slice(0, 10)
}

// Inverse of zonedWallClock: given a naive local wall-clock string and a timezone,
// return the UTC instant. DST-safe for day boundaries via the offset-delta trick.
function wallClockToUtc(localIso: string, timezone: string): Date {
  const asIfUtc = new Date(`${localIso}Z`)
  const backWall = zonedWallClock(asIfUtc.toISOString(), timezone)
  const offsetMs = asIfUtc.getTime() - new Date(`${backWall}Z`).getTime()
  return new Date(asIfUtc.getTime() + offsetMs)
}

// UTC bounds [from, to] (ISO) covering one full tenant-local calendar day.
export function localDayBoundsUtc(localDate: string, timezone: string): { from: string; to: string } {
  return {
    from: wallClockToUtc(`${localDate}T00:00:00`, timezone).toISOString(),
    to: wallClockToUtc(`${localDate}T23:59:59`, timezone).toISOString(),
  }
}

// ─── Birthday week ─────────────────────────────────────────────────────────────
//
// The operator asked to see a birthday symbol "during the client's birthday week,
// from seven days before to seven days after their birthday" (2026-08-17).
//
// Pure string arithmetic on two plain dates — no `Date` built from a naive
// datetime, no timezone anywhere. Both inputs are already calendar dates: the dob
// as stored, and today in the TENANT's zone (`todayLocalDate`). Using the
// browser's today would show the symbol a day early or late for an operator
// working from a different country.

/** Days from `today` to the nearest occurrence of this birthday. Negative = past. */
export function daysToBirthday(dob: string, today: string): number | null {
  const dobMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob)
  const todayMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(today)
  if (!dobMatch || !todayMatch) return null

  const month = Number(dobMatch[2])
  const day = Number(dobMatch[3])
  const [ty, tm, td] = [Number(todayMatch[1]), Number(todayMatch[2]), Number(todayMatch[3])]
  const todayMs = Date.UTC(ty, tm - 1, td)

  // Three candidate years, because the window straddles New Year: a 28 December
  // birthday is four days away on 1 January, and testing only the current year
  // would call it 361 days away and hide the symbol.
  let nearest: number | null = null
  for (const year of [ty - 1, ty, ty + 1]) {
    // 29 February in a common year rolls to 1 March, which is the conventional
    // answer rather than an error.
    const diff = Math.round((Date.UTC(year, month - 1, day) - todayMs) / 86_400_000)
    if (nearest === null || Math.abs(diff) < Math.abs(nearest)) nearest = diff
  }
  return nearest
}

/** Seven days either side of the birthday, inclusive. */
export function isBirthdayWeek(dob: string | null | undefined, today: string): boolean {
  if (!dob) return false
  const days = daysToBirthday(dob, today)
  return days !== null && Math.abs(days) <= 7
}

/** 'Birthday today' / 'Birthday in 3 days' / 'Birthday was 2 days ago', or null. */
export function birthdayLabel(dob: string | null | undefined, today: string): string | null {
  if (!dob) return null
  const days = daysToBirthday(dob, today)
  if (days === null || Math.abs(days) > 7) return null
  if (days === 0) return 'Birthday today'
  if (days > 0) return `Birthday in ${days} day${days === 1 ? '' : 's'}`
  const ago = Math.abs(days)
  return `Birthday was ${ago} day${ago === 1 ? '' : 's'} ago`
}

// A credit-covered session reads as PAID. amount_paid stays 0 on it by design —
// the cash arrived when the package was sold — so deriving payment state from
// amount_paid alone showed every package customer as owing money.
function paymentStatus(price: unknown, amountPaid: unknown, packageDebited: boolean): PaymentStatus | undefined {
  if (typeof price !== 'number' || price <= 0) return undefined
  if (packageDebited) return 'paid'
  const paid = typeof amountPaid === 'number' ? amountPaid : 0
  if (paid <= 0) return 'unpaid'
  if (paid >= price) return 'paid'
  return 'partial'
}

// Transform raw bookings + resources into the CalendarProps the day view expects.
export function bookingsToCalendarProps(
  bookings: BookingRecord[],
  resources: ResourceRecord[],
  localDate: string,
  timezone: string,
): CalendarProps {
  const resourceById = new Map<string, ResourceRecord>()
  for (const r of resources) resourceById.set(r.id, r)

  const events: CalendarEvent[] = bookings.map(b => {
    const ids = Array.isArray(b.resource_ids) ? b.resource_ids : []
    const matched = ids.map(id => resourceById.get(id)).filter(Boolean) as ResourceRecord[]
    const orders = matched
      .map(r => (typeof r.display_order === 'number' ? r.display_order : undefined))
      .filter((n): n is number => n !== undefined)
    return {
      id: b.id,
      start: zonedWallClock(b.start, timezone),
      end: zonedWallClock(b.end, timezone),
      service_name: b.services?.name ?? 'Booking',
      service_order: b.services?.display_order,
      customer_name: b.customers?.name ?? undefined,
      customer_id: b.customers?.id ?? b.customer_id ?? undefined,
      status: (b.status as BookingStatus) ?? 'booked',
      payment_status: paymentStatus(b.price, b.amount_paid, b.package_debited === true),
      paid_with_package: b.package_debited === true,
      // Already tenant-local naive from the API, so they pass through untouched —
      // running them through zonedWallClock would be a second conversion.
      checked_in_at: b.checked_in_at ?? null,
      checked_out_at: b.checked_out_at ?? null,
      resource_ids: ids,
      resource_name: matched.length ? matched.map(r => r.name).join(', ') : undefined,
      resource_order: orders.length ? Math.min(...orders) : undefined,
    }
  })

  // Callers pass retired resources too, because a past booking still names the
  // machine it ran on and the tile would otherwise lose its label. They must not
  // all become columns, though — every machine ever decommissioned would leave an
  // empty lane on the grid forever. A retired resource earns its column only while
  // a booking in view is still on it. `is_active` absent means active.
  const referenced = new Set(bookings.flatMap(b => (Array.isArray(b.resource_ids) ? b.resource_ids : [])))
  const calendarResources: CalendarResource[] = resources
    .filter(r => r.is_active !== false || referenced.has(r.id))
    .map(r => ({
      id: r.id,
      name: r.name,
      type: r.type,
      display_order: r.display_order,
      // Carried through so click-to-book can refuse to offer bands on a retired
      // machine that only kept its column to label an old booking.
      is_active: r.is_active,
    }))

  return { view: 'day', date: localDate, events, resources: calendarResources }
}
